'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaLongArrowAltRight, FaTimes } from 'react-icons/fa'

const GemsDiscountBanner = ({ plan }) => {
  const { t } = useTranslation()
  const blackFridayDiscount =
    (process.env.NEXT_PUBLIC_BLACK_FRIDAY ?? '') === 'true'
  const [timeLeft, setTimeLeft] = useState('')

  const [isVisible, setIsVisible] = useState(true)

  const handleClose = () => {
    setIsVisible(false)
  }

  useEffect(() => {
    if (!blackFridayDiscount) return
    const target = Number(process.env.NEXT_PUBLIC_BLACK_FRIDAY_TARGET)
    const timer = setInterval(() => {
      const diff = target - Date.now()
      if (diff <= 0) {
        setTimeLeft('0d 0h 0m 0s')
        clearInterval(timer)
        return
      }
      const d = Math.floor(diff / (1000 * 60 * 60 * 24))
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24)
      const m = Math.floor((diff / (1000 * 60)) % 60)
      const s = Math.floor((diff / 1000) % 60)
      setTimeLeft(`${d}d ${h}h ${m}m ${s}s`)
    }, 1000)
    return () => clearInterval(timer)
  }, [blackFridayDiscount])

  const percentOff = '50%'
  const countdownParts = timeLeft
    ? timeLeft.split(' ').map((part) => ({
        value: part.slice(0, -1),
        label: part.slice(-1),
      }))
    : []

  if (!isVisible) {
    return null
  }

  return (
    <div className="relative border-b border-white/10 shadow-sm flex items-center justify-center py-1 text-white bg-[var(--background-bg-color)]">
      <div className="relative z-10 mx-auto flex w-full max-w-5xl items-center justify-center gap-1.5 pl-3 pr-8 sm:gap-3 sm:px-9">
        <div className="flex min-w-0 items-center gap-2">
          <h2
            className={`
    ${blackFridayDiscount ? `text-white` : 'text-white'}
    truncate font-semibold text-sm sm:text-base flex items-center justify-center whitespace-nowrap
  `}
          >
            {blackFridayDiscount ? (
              <>
                <span className="sm:hidden">{t('summerSaleTitleShort')}</span>
                <span className="hidden sm:inline">
                  {t('blackFridayBanner')}
                </span>
              </>
            ) : (
              t('gemsSaleTitle')
            )}
          </h2>
        </div>

        {timeLeft && blackFridayDiscount && (
          <div className="flex shrink-0 items-center justify-center">
            <div
              className="inline-flex items-center overflow-hidden rounded-lg border border-white/10 bg-[var(--foreground-bg-color)] shadow-sm"
              aria-live="polite"
              title={t('offerCountdown')}
            >
              <span className="hidden px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:inline">
                {t('summerSaleEndsIn')}
              </span>
              {countdownParts.map(({ value, label }, index) => (
                <span
                  key={label}
                  className="flex items-baseline gap-0.5 border-l border-white/10 px-0.5 py-1 leading-none sm:px-1.5"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <span className="inline-block min-w-[2ch] text-center text-[11px] font-bold text-white">
                    {value}
                  </span>
                  <span className="text-[9px] font-semibold uppercase text-gray-400">
                    {label}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        <Link
          href="/gems"
          aria-label={
            blackFridayDiscount
              ? t('summerSaleCta')
              : t('claimOff', { percent: percentOff })
          }
          className="shrink-0 font-bold"
        >
          <div className="flex items-center justify-center rounded-xl border border-gray-500/40 bg-[var(--main-bg-color)] px-2.5 py-1 text-center text-xs font-semibold text-white shadow-sm transition duration-200 hover:scale-[1.03] hover:border-gray-400/60 hover:bg-[var(--main-shade-bg-color)] sm:px-4 sm:text-sm">
            <p className="whitespace-nowrap">
              {blackFridayDiscount ? (
                <>
                  <span className="sm:hidden">{t('summerSaleCtaShort')}</span>
                  <span className="hidden sm:inline">{t('summerSaleCta')}</span>
                </>
              ) : (
                t('claimOff', { percent: percentOff })
              )}
            </p>
          </div>
        </Link>

        {!blackFridayDiscount && (
          <Link href="/gems" className="flex items-center md:block hidden">
            <button className="text-gray-300 font-medium py-2 flex items-center justify-center ml-3 text-xs">
              {t('seePricing')} <FaLongArrowAltRight className="ml-1" />
            </button>
          </Link>
        )}
      </div>
      <button
        onClick={handleClose}
        className="absolute top-0 right-0 z-10 mt-2 mr-2 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white/60 hover:bg-white/10 hover:text-white"
      >
        <FaTimes />
      </button>
    </div>
  )
}

export default GemsDiscountBanner
